import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PetName from "@/app/components/ui/Reusable/PetName";
import Species from "@/app/components/ui/Reusable/Species";
import Username from "@/app/components/ui/Reusable/Username";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect } from "react";
import { MdOutlineFileUpload } from "react-icons/md";

const JobCardClosingComponent = () => {
  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();
  const { setVisible: uploadDocument, setModalData } = useModalActions("CERTIFICATE_DOCUMENT_UPLOAD_MODAL");

  const { veticEpocToTime } = useUtils();

  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();

  // ✅ read query params
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const time = queryParams.get("t") ?? "";

  useEffect(() => {
    if (queryClinics && queryClinics.length > 0) {
      fetchDayClosingData(queryClinics, queryShift, "appointments", "document");
    }
  }, [processId, queryCity, queryClinics, queryShift, time]);
  const clinicsWithDocuments = closureData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  const handleCertificateDocumentUploadClick = () => {
    uploadDocument();
  };

  return (
    <>
      <div className="w-full pb-4">
        {isLoading ? (
          <TableSkeletonComponent columnLength={8} />
        ) : clinicsWithDocuments.length > 0 ? (
          <div className="space-y-3 overflow-y-auto max-h-[650px]">
            {clinicsWithDocuments.map((clinic) => (
              <div key={clinic.clinic_id} className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm">
                <div className="overflow-x-auto rounded-xl">
                  <table className="w-full min-w-[1150px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                          <tr>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Client Name</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Pet Details</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Appointment Type</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Time</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Service Provider</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Document Type</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Actions</th>
                          </tr>
                        </thead>

                        <tbody className="text-xs">
                          {clinic.closure_list.map((item, rowIndex) => {
                            const isFirst = rowIndex === 0;
                            const isLast = rowIndex === clinic.closure_list.length - 1;
                            const isSingle = clinic.closure_list.length === 1;

                            return (
                            <tr
                              key={item.booking_id}
                              className={`
                                bg-white hover:bg-[#FCFCFD] transition-colors
                                ${isSingle ? "rounded-xl" : ""}
                                ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                ${isLast && !isSingle ? "rounded-b-xl" : ""}
                              `}
                            >
                              <td
                                className={`
                                  p-3 text-xs text-blue-600
                                  ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                `}
                              >
                                <Username
                                  username={item.user_details?.name}
                                  petId={item.pet_details?.id}
                                  userId={item.user_details?.id}
                                />
                              </td>

                              <td className="p-3 text-xs">
                                <div className="flex flex-col gap-1">
                                  <PetName petName={item.pet_details?.name} className="text-xs" petStatus="" />
                                  <Species
                                    speciesId={item.pet_details?.pet_species_type_id}
                                    className="text-[#6C727F] text-xs"
                                  />
                                </div>
                              </td>

                              <td className="p-3 text-[10px] sm:text-xs text-black ">
                                <p>{item.service_details?.service_type}</p>
                              </td>

                              <td className="p-3 text-xs text-[#6C727F] whitespace-nowrap">
                                {veticEpocToTime(item?.booking_start_date_time)}
                              </td>

                              <td className="p-3 text-xs">{item.staff_details?.name || "-"}</td>
                              <td className="p-3 text-xs">{item.document_types?.join(", ") || "-"}</td>

                              <td
                                className={`
                                  p-3 text-xs w-[150px]
                                  ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                `}
                              >
                                {!item?.closure_status && (
                                  <div className="flex flex-row items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setModalData(item);
                                        handleCertificateDocumentUploadClick();
                                      }}
                                      className="bg-black px-4 py-2 rounded-lg text-[10px] sm:text-xs text-white font-medium flex gap-2 items-center whitespace-nowrap hover:opacity-90 transition-opacity"
                                    >
                                      <MdOutlineFileUpload className="w-4 h-4" />
                                      Upload
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                  </div>
                  </div>
            ))}
          </div>
        ) : (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Appointments Found" />
        )}
      </div>
    </>
  );
};

export default JobCardClosingComponent;
